import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import Vue from 'vue'
import axios from 'axios'
import apiEndpoints from '@/constants/apiEndpoints'
import router from '../../../../router'

export default function useBranchsList() {
  const toast = useToast()
  const refBranchListTable = ref(null)

  const tableColumns = [
    { key: 'branch_name', sortable: true },
    { key: 'branch_code', sortable: true },
    { key: 'branch_city', sortable: true },
    { key: 'branch_region' },
    { key: 'branch_status' },
    { key: 'actions' },
  ]

  const perPage = ref(10)
  const totalBranchs = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('branch_name')
  const isSortDirDesc = ref(true)
  const roleFilter = ref(null)
  const statusFilter = ref(null)
  const loading = ref(false)
  const branchStats = ref({ total: 0, active: 0, inactive: 0 })
  const statsLoading = ref(false)

  const dataMeta = computed(() => {
    const localItemsCount = refBranchListTable.value ? refBranchListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalBranchs.value,
    }
  })

  const refetchData = () => {
    if (refBranchListTable.value) {
      refBranchListTable.value.refresh()
    }
  }

  watch([currentPage, perPage, searchQuery, roleFilter, statusFilter, sortBy, isSortDirDesc], () => {
    refetchData()
  })

  const fetchBranchs = (ctx, callback) => {
    const to = perPage.value * (currentPage.value - 1)
    const userToken = Vue.$cookies.get('userToken')
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userToken}`,
    }

    loading.value = true
    axios
      .get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_BRANCHES}`, {
        headers,
        params: {
          search: searchQuery.value,
          order_by: sortBy.value,
          order_sort: isSortDirDesc.value ? 'DESC' : 'ASC',
          limit: perPage.value,
          offset: to,
        },
      })
      .then(response => {
        if (response.data && response.data.data !== null) {
          totalBranchs.value = response.data.data_count || 0
          callback(response.data.data)
        } else {
          callback([])
        }
        loading.value = false
      })
      .catch(e => {
        if (e.message === 'Request failed with status code 401') {
          router.push({ name: 'auth-login' })
        } else {
          console.error(e)
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error fetching data list',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        }
        loading.value = false
      })
  }

  const fetchBranchStats = () => {
    const userToken = Vue.$cookies.get('userToken')
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userToken}`,
    }

    statsLoading.value = true
    return axios
      .get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_BRANCH_STATS}`, { headers })
      .then(response => {
        if (response.data && response.data.data) {
          branchStats.value = response.data.data
        }
      })
      .catch(e => {
        console.error(e)
      })
      .finally(() => {
        statsLoading.value = false
      })
  }

  const toggleBranchStatus = branchId => {
    const userToken = Vue.$cookies.get('userToken')
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userToken}`,
    }

    return axios
      .get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_TOGGLE_BRANCH_STATUS}/${branchId}`, { headers })
      .then(response => response.data)
  }

  const fetchRoles = () => {
    const userToken = Vue.$cookies.get('userToken')
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userToken}`,
    }

    return axios
      .get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_ROLES}`, { headers })
      .then(response => {
        if (response.data && response.data.data) {
          return response.data.data.map(elem => ({
            label: elem.role_name,
            value: elem.role_id,
          }))
        }
        return [
          { label: 'Admin', value: 'admin' },
          { label: 'Author', value: 'author' },
          { label: 'Editor', value: 'editor' },
          { label: 'Maintainer', value: 'maintainer' },
          { label: 'Subscriber', value: 'subscriber' },
        ]
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching role list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return []
      })
  }

  return {
    fetchBranchs,
    fetchRoles,
    fetchBranchStats,
    toggleBranchStatus,
    tableColumns,
    perPage,
    currentPage,
    totalBranchs,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refBranchListTable,
    refetchData,
    branchStats,
    statsLoading,

    // Extra Filters
    roleFilter,
    statusFilter,
    loading,
  }
}